import React, { FunctionComponent, ReactNode, useContext } from 'react'
import { Accordion, Heading, useThemePerClass, DesignLanguage, useIsDesignLanguage, Theme, useIsTheme } from '@afterpaytouch/core'
import { useAllowedShippingOptions, useIsDisabledUpdatingCheckout, useSelectedShippingOptionAllowed, useShippingOption } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import styles from './style.module.css'
import { ShippingOptionContext } from './context'
import { ShippingOption } from '@afterpaytouch/portal-api/types'
import { ShippingOptionStatus } from '../../model'
import { ExpandableComponent } from '../../model'
import { ModuleContainer } from '../ModuleContainer'
import { useSummaryHeaderLabel } from './hooks'
import { ShippingOptionHeader } from './ShippingOptionHeader'

interface Props {
  children: ReactNode
  selectedShippingOption?: ShippingOption
  hasAllowedShippingOptions: boolean
}

export const ShippingOptionWrapper: FunctionComponent<Props> = ({ children, selectedShippingOption, hasAllowedShippingOptions }) => {
  const themedClass = useThemePerClass(styles)
  const { shippingOptionsStatus } = useShippingOption()
  const isDisabled = useIsDisabledUpdatingCheckout()
  const { isExpanded, handleExpandedStateChange } = useContext(ShippingOptionContext)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { textSize } = useCheckoutSizes()
  const summaryHeadingLabel = useSummaryHeaderLabel(hasAllowedShippingOptions, selectedShippingOption)
  const allowedShippingOptions = useAllowedShippingOptions()
  const isSelectedShippingOptionExist = useSelectedShippingOptionAllowed(allowedShippingOptions)

  const isPegasusTheme = useIsTheme(Theme.Pegasus)
  const isKelpieTheme = useIsTheme(Theme.Kelpie)
  const isKelpieMintTheme = useIsTheme(Theme.KelpieMint)
  const isPegasusOrKelpieTheme = isPegasusTheme || isKelpieTheme || isKelpieMintTheme

  if (isArcadeDesignLanguage) {
    if (shippingOptionsStatus === ShippingOptionStatus.Error) {
      return (
        <>
          <ShippingOptionHeader selectedShippingOption={selectedShippingOption} />
          <ModuleContainer padding={{ vertical: 'Small' }} noBottomMargin showBorder={false} reducedHeight>
            {children}
          </ModuleContainer>
        </>
      )
    }

    if (isPegasusOrKelpieTheme && allowedShippingOptions.length === 1 && isSelectedShippingOptionExist) {
      return <>{children}</>
    }

    return (
      <div className={themedClass.shippingOptionContainer} data-testid='shipping-option-wrapper-container'>
        <Accordion
          id={ExpandableComponent.SHIPPING_OPTION}
          onChange={handleExpandedStateChange}
          expanded={isExpanded}
          verticalAlignLabel={isArcadeDesignLanguage}
          disabled={isDisabled}
        >
          <Accordion.Button>
            <ShippingOptionHeader selectedShippingOption={selectedShippingOption} />
          </Accordion.Button>
          <Accordion.Details>{children}</Accordion.Details>
        </Accordion>
      </div>
    )
  }

  return (
    <>
      <Heading size={textSize}>{summaryHeadingLabel}</Heading>
      <div className={themedClass.shippingOptionContainer} data-testid='shipping-option-wrapper-container'>
        {children}
      </div>
    </>
  )
}
